/**
 * Typed API client for the candidate-pool backend.
 *
 * Conventions (mirrors docs/phase-3-api.md §11.1 + Phase 5b auth/cookies design):
 *   - Cross-origin fetch with `credentials: 'include'` so the three pool cookies travel.
 *   - Mutations echo the `qh_candidate_csrf` cookie value into the `x-qh-csrf` header
 *     (double-submit cookie pattern; Phase 5b backend issues + verifies it).
 *   - Backend responses are wrapped in `{ data: ... }` on success and
 *     `{ error: { code, message, details, correlation_id } }` on failure — both forms
 *     are unwrapped here so callers see clean payloads / typed errors.
 *
 * The auth-specific helpers at the bottom of this file are the only place that
 * names the candidate auth routes; everything else uses the generic `get/post/...` methods.
 */

const RAW_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3100/api/v1').trim();
export const API_BASE_URL = RAW_BASE_URL.replace(/\/+$/, '');

const CANDIDATE_CSRF_COOKIE = 'qh_candidate_csrf';
const CSRF_HEADER = 'x-qh-csrf';

// ---------- Types ----------

export interface ApiErrorDetail {
  field?: string;
  issue?: string;
  [k: string]: unknown;
}

export interface ApiErrorPayload {
  code: string;
  message: string;
  correlation_id?: string;
  details?: ApiErrorDetail[];
}

/**
 * Thrown for any non-2xx response (including network/parse errors mapped to a synthetic envelope).
 * Callers catch this to surface meaningful messages from `error.message` directly.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly correlationId: string | undefined;
  readonly details: ApiErrorDetail[] | undefined;

  constructor(payload: ApiErrorPayload, status: number) {
    super(payload.message);
    this.name = 'ApiError';
    this.code = payload.code;
    this.status = status;
    this.correlationId = payload.correlation_id;
    this.details = payload.details;
  }
}

/**
 * Specialised ApiError for HTTP 429 / RATE_LIMITED. Exposes `retryAfterSeconds` parsed from
 * the canonical envelope (`details: [{ retry_after_seconds }]`) or the `Retry-After` header.
 * Forms render an inline countdown using this; browse pages show a soft banner.
 */
export class RateLimitError extends ApiError {
  readonly retryAfterSeconds: number;

  constructor(payload: ApiErrorPayload, status: number, retryAfterSeconds: number) {
    super(payload, status);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export type RegionSlug = 'india' | 'usa' | 'canada' | 'thailand';

export interface CandidateMe {
  id: string;
  email: string;
  email_verified_at: string | null;
  status: 'ACTIVE' | 'SUSPENDED';
  region: { slug: RegionSlug; name: string };
  full_name: string;
  has_primary_resume: boolean;
  oauth_providers: string[];
  /** Phase 7-dashboard-a — drives the profile-completion gate. */
  profile_completed: boolean;
}

/**
 * Phase 7-dashboard-a: signin returns one of two envelopes. `'active'` is the normal
 * authenticated session; `'recoverable'` means the candidate's row is soft-deleted and
 * the response carries a short-lived recovery cookie instead of session cookies — the
 * frontend redirects to `/recover-account` and surfaces the one-click restore CTA there.
 */
export type SigninResult =
  | { kind: 'active'; candidate: CandidateMe }
  | {
      kind: 'recoverable';
      code: 'ACCOUNT_DELETED_RECOVERABLE';
      deleted_at: string;
      days_in_soft_delete: number;
    };

// ---------- Internal helpers ----------

/**
 * Pulls the retry-after seconds from the response. Backend's RATE_LIMITED envelope carries
 * it under `details[0].retry_after_seconds`; the `Retry-After` header is a fallback. Floors
 * to 1 second so the UI countdown never displays "0 seconds" on first render.
 */
function parseRetryAfter(res: Response, error: ApiErrorPayload | null): number {
  if (error?.details && error.details.length > 0) {
    const v = (error.details[0] as { retry_after_seconds?: unknown }).retry_after_seconds;
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return Math.max(1, Math.ceil(v));
  }
  const header = res.headers.get('retry-after');
  if (header) {
    const n = Number(header);
    if (Number.isFinite(n) && n > 0) return Math.max(1, Math.ceil(n));
  }
  return 60;
}

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const all = document.cookie ? document.cookie.split('; ') : [];
  for (const entry of all) {
    const eq = entry.indexOf('=');
    if (eq < 0) continue;
    if (entry.slice(0, eq) === CANDIDATE_CSRF_COOKIE) {
      return decodeURIComponent(entry.slice(eq + 1));
    }
  }
  return null;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the `{ data: ... }` envelope unwrap — used for 204-style endpoints. */
  raw?: boolean;
  /**
   * Auto-retry on `RATE_LIMITED` responses with exponential backoff (1s, then 3s).
   * Default: true for GET requests, false for everything else. Mutations should fail fast
   * so the form's countdown UI can show the user what's happening.
   */
  retryOnRateLimit?: boolean;
}

/**
 * Mini Phase 6.5 retry-with-backoff: when a browse-style GET hits a transient rate-limit
 * spike (e.g., StrictMode + rapid navigation in dev), wait briefly and try again rather
 * than surfacing a 429 to the user. Two retries total → max worst-case delay 4s before the
 * caller sees an error. The callsite still gets a `RateLimitError` if all attempts fail.
 */
const RETRY_BACKOFF_MS = [1000, 3000] as const;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const shouldRetry = opts.retryOnRateLimit ?? method === 'GET';
  let attempt = 0;
  // Loop runs at most `1 + RETRY_BACKOFF_MS.length` times (initial + retries).
  // Non-RATE_LIMITED errors short-circuit immediately.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await requestOnce<T>(method, path, body, opts);
    } catch (err) {
      if (
        shouldRetry &&
        err instanceof RateLimitError &&
        attempt < RETRY_BACKOFF_MS.length
      ) {
        await sleep(RETRY_BACKOFF_MS[attempt]);
        attempt += 1;
        continue;
      }
      throw err;
    }
  }
}

async function requestOnce<T>(
  method: Method,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const url = new URL(`${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`);
  if (opts.query) {
    for (const [key, value] of Object.entries(opts.query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  // Double-submit CSRF: every mutation must echo the cookie into the header.
  if (method !== 'GET') {
    const csrf = readCsrfCookie();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  // Public endpoints (/public/*) are intentionally cookieless from the website per Phase 5b
  // hardening — keeps the public surface stateless and edge-cacheable. Side effect: the
  // public job detail endpoint can read OptionalCandidate but won't get the cookie, so its
  // `applicability.has_applied` is always false for signed-in candidates. JobDetailPage
  // hydrates this separately via `candidateApi.applications.hasAppliedToJob()` against the
  // authenticated /candidates/me/applications endpoint.
  const isPublicPath = url.pathname.startsWith('/api/v1/public/') || url.pathname === '/api/v1/public';

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method,
      credentials: isPublicPath ? 'omit' : 'include',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    throw new ApiError(
      {
        code: 'NETWORK_ERROR',
        message: 'Could not reach the QuantumHire server. Check your connection and try again.',
      },
      0,
    );
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const envelope = payload as { error?: ApiErrorPayload } | null;
    if (envelope && envelope.error) {
      if (envelope.error.code === 'RATE_LIMITED') {
        throw new RateLimitError(envelope.error, res.status, parseRetryAfter(res, envelope.error));
      }
      throw new ApiError(envelope.error, res.status);
    }
    if (res.status === 429) {
      throw new RateLimitError(
        { code: 'RATE_LIMITED', message: 'Too many requests. Please slow down and try again shortly.' },
        res.status,
        parseRetryAfter(res, null),
      );
    }
    throw new ApiError(
      {
        code: 'INTERNAL_ERROR',
        message: 'Something went wrong. Please try again.',
      },
      res.status,
    );
  }

  if (opts.raw) return payload as T;
  const dataEnv = payload as { data?: T } | null;
  return (dataEnv && 'data' in dataEnv ? (dataEnv.data as T) : (payload as T));
}

// ---------- Generic verbs ----------

export const apiClient = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, body, opts),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
};

// ---------- Candidate auth helpers ----------

export interface SignupInput {
  full_name: string;
  email: string;
  password: string;
  region_slug: RegionSlug;
}

export interface SigninInput {
  email: string;
  password: string;
}

export interface GoogleSignupInput {
  id_token: string;
  region_slug: RegionSlug;
}

export interface GoogleSigninInput {
  id_token: string;
}

export interface ResetPasswordInput {
  token: string;
  password: string;
}

export interface VerifyEmailResponse {
  verified_at: string;
  candidate: CandidateMe;
}

/**
 * Phase 7-dashboard hotfix round 3 — email/password signup no longer signs the
 * candidate in. The `pending_verification` envelope tells the SignUp page to render
 * a "check your inbox" screen rather than calling `setCandidate(...)`.
 */
export interface SignupPendingVerificationResponse {
  pending_verification: true;
  email: string;
}

interface CandidateEnvelope {
  candidate: CandidateMe;
}

interface RecoverableSigninEnvelope {
  code: 'ACCOUNT_DELETED_RECOVERABLE';
  deleted_at: string;
  days_in_soft_delete: number;
}

export const auth = {
  me: (): Promise<CandidateMe> => apiClient.get<CandidateMe>('/candidates/me'),

  signup: async (input: SignupInput): Promise<SignupPendingVerificationResponse> => {
    return apiClient.post<SignupPendingVerificationResponse>('/candidates/auth/signup', input);
  },

  /**
   * Phase 7-dashboard-a: signin can return a recoverable envelope when the candidate's
   * row is soft-deleted. Caller branches on `result.kind`.
   */
  signin: async (input: SigninInput): Promise<SigninResult> => {
    const env = await apiClient.post<CandidateEnvelope | RecoverableSigninEnvelope>(
      '/candidates/auth/signin',
      input,
    );
    if ('code' in env && env.code === 'ACCOUNT_DELETED_RECOVERABLE') {
      return {
        kind: 'recoverable',
        code: env.code,
        deleted_at: env.deleted_at,
        days_in_soft_delete: env.days_in_soft_delete,
      };
    }
    return { kind: 'active', candidate: (env as CandidateEnvelope).candidate };
  },

  signupWithGoogle: async (input: GoogleSignupInput): Promise<CandidateMe> => {
    const env = await apiClient.post<CandidateEnvelope>('/candidates/auth/signup/google', input);
    return env.candidate;
  },

  signinWithGoogle: async (input: GoogleSigninInput): Promise<CandidateMe> => {
    const env = await apiClient.post<CandidateEnvelope>('/candidates/auth/signin/google', input);
    return env.candidate;
  },

  signout: (): Promise<void> => apiClient.post<void>('/candidates/auth/signout'),

  forgotPassword: (email: string): Promise<void> =>
    apiClient.post<void>('/candidates/auth/forgot-password', { email }),

  resetPassword: async (input: ResetPasswordInput): Promise<CandidateMe> => {
    const env = await apiClient.post<CandidateEnvelope>('/candidates/auth/reset-password', input);
    return env.candidate;
  },

  verifyEmail: (token: string): Promise<VerifyEmailResponse> =>
    apiClient.post<VerifyEmailResponse>('/candidates/auth/verify-email', { token }),

  changePassword: (input: { current_password: string; new_password: string }): Promise<void> =>
    apiClient.post<void>('/candidates/auth/change-password', input),

  /**
   * Recruitment workflow §6.3 — claim an unclaimed sourced account from its invite link: set
   * the first password + declarations, verify the email, open a session. Lives under
   * /candidates/auth (credentialed) so the session cookies it sets are kept.
   */
  claimInvite: async (token: string, input: ClaimInviteInput): Promise<CandidateMe> => {
    const env = await apiClient.post<CandidateEnvelope>(
      `/candidates/auth/invites/${encodeURIComponent(token)}/claim`,
      input,
    );
    return env.candidate;
  },
};

export interface ClaimInviteInput {
  password: string;
  fraud_declaration_consent: true;
  rtr_consent?: boolean | null;
}

/** GET /public/invites/:token — what the token-gated invite page shows (§6.3). */
export type InvitePageState = 'available' | 'job_unavailable' | 'already_applied';

export interface InvitePageData {
  state: InvitePageState;
  invitee: { full_name: string; email: string; account_state: 'unclaimed' | 'password' | 'google' };
  job: { id: string; title: string; slug: string; region_name: string };
  /**
   * The client company — shown ONLY on this token-gated page and in the invite email, never
   * on public pages (§6.2). Null unless the job is available.
   */
  company: {
    name: string;
    logo_url: string | null;
    tagline: string | null;
    about: string | null;
    why_work_here: string[];
    work_environment: string[];
    website: string | null;
    founded_year: number | null;
    industry: string | null;
    company_size: string | null;
    hq_city: string | null;
  } | null;
}

// ---------- Public + applications shapes ----------

export interface MasterDataItem {
  id: string;
  slug: string;
  name: string;
  sort_order?: number;
}

export interface RegionItem extends MasterDataItem {
  country_code: string;
  currency: { code: string; symbol: string };
}

export interface PaginationMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface JobLocation {
  slug: string;
  name: string;
}

export interface JobSalaryVisible {
  visible: true;
  min: number;
  max: number;
  currency: { code: string; symbol: string } | null;
  pay_period: { slug: string; name: string } | null;
}

export interface JobSalaryHidden {
  visible: false;
}

export type JobSalary = JobSalaryVisible | JobSalaryHidden;

/**
 * A public job. Carries nothing about the client company (recruitment workflow §6.2): the client
 * is named only in the invite email, on the token-gated invite page (`InvitePageData`) and in the
 * interview-invite email.
 */
export interface JobSummary {
  id: string;
  slug: string;
  title: string;
  region: JobLocation;
  cities: JobLocation[];
  employment_type: JobLocation | null;
  work_arrangement: JobLocation | null;
  experience_level: JobLocation | null;
  salary: JobSalary;
  published_at: string | null;
  valid_through: string | null;
  has_applied: boolean;
}

export type ApplyBlockedReason =
  | null
  | 'NOT_AUTHENTICATED'
  | 'EMAIL_NOT_VERIFIED'
  | 'ACCOUNT_SUSPENDED'
  | 'REGION_MISMATCH'
  | 'ALREADY_APPLIED'
  | 'JOB_NOT_APPLICABLE';

export interface ApplicabilityBlock {
  has_applied: boolean;
  can_apply: boolean;
  apply_blocked_reason: ApplyBlockedReason;
}

export interface JobDetail extends JobSummary {
  description_html: string;
  job_category: JobLocation | null;
  min_experience_months: number | null;
  max_experience_months: number | null;
  education_requirements: string | null;
  experience_requirements: string | null;
  qualifications: string | null;
  responsibilities: string | null;
  skills: { slug: string; name: string; is_required: boolean }[];
  date_posted: string | null;
  identifier: string;
  applicability: ApplicabilityBlock;
}

export interface CompanySignupBody {
  company: {
    name: string;
    website?: string;
    industry_slug?: string;
    company_size_slug?: string;
    founded_year?: number;
    hq_city_slug?: string;
    description?: string;
    region_slugs: string[];
    estimated_hiring_volume?: string;
  };
  admin: {
    full_name: string;
    email: string;
    phone?: string;
  };
  consent: {
    accepted_privacy_policy: true;
    accepted_terms: true;
  };
}

export interface CompanySignupResponse {
  company_id: string;
  submitted_at: string;
  next_steps: string;
}

export interface CreateApplicationInput {
  job_id: string;
  resume_id?: string;
  cover_note?: string;
  /**
   * Recruitment workflow §6.4 — the invite token saved by the invite page for this job. Counts
   * only for the signed-in candidate's own invite; any other token is ignored by the server.
   */
  invite_token?: string;
}

export interface CreatedApplication {
  id: string;
  job_id: string;
  status: string;
  applied_at: string;
}

export interface JobsListEnvelope {
  data: JobSummary[];
  meta: PaginationMeta;
}

export interface PublicResourceAuthor {
  slug: string;
  name: string;
  designation: string | null;
  company: string | null;
  photo_url: string | null;
  is_guest: boolean;
}

export interface PublicResource {
  id: string;
  slug: string;
  type: string;
  category: string;
  title: string;
  excerpt: string | null;
  publishedAt: string | null;
  readTime: number | null;
  duration: number | null;
  videoUrl: string | null;
  stat: string | null;
  statLabel: string | null;
  body: unknown;
  author: PublicResourceAuthor;
}

export interface PublicResourceDetail extends PublicResource {
  related_resources: PublicResource[];
}

export interface ResourcesListEnvelope {
  data: PublicResource[];
  meta: PaginationMeta;
}

export interface JobsListFilters {
  q?: string;
  region?: string;
  city?: string;
  job_category?: string;
  employment_type?: string;
  work_arrangement?: string;
  experience_level?: string;
  page?: number;
  page_size?: number;
}

// ---------- Public read helpers ----------

export interface StartupInput {
  name: string;
  email: string;
  company: string;
  role: string;
  stage?: string;
  teamSize?: string;
  engagement?: string;
  details?: string;
}

export interface CareerRestartInput {
  name: string;
  email: string;
  phone: string;
  city?: string;
  breakLength?: string;
  targetRole?: string;
}

export interface ExFounderInquiryInput {
  name: string;
  email: string;
  linkedin_url: string;
  comments: string;
}

export interface PublicSubmissionResponse {
  id: string;
  message: string;
}

export interface ProductDemoInput {
  name: string;
  email: string;
  company: string;
  teamSize?: string;
  note?: string;
}

export interface ScreenerInput {
  name: string;
  email: string;
  linkedinUrl: string;
  currentRole: string;
  yearsInDomain: string;
  screeningDomains: string[];
  hoursPerWeek?: string;
  timeZone?: string;
  notes?: string;
}

export interface FeatureNominationInput {
  person_name: string;
  person_title_company: string;
  reason: string;
  submitted_by_name: string;
  submitted_by_email: string;
  linkedin_url?: string;
}

export const publicApi = {
  /** Recruitment workflow §6.3 — the invite page read; 404 for any link that isn't valid. */
  invite: (token: string): Promise<InvitePageData> =>
    apiClient.get<InvitePageData>(`/public/invites/${encodeURIComponent(token)}`),

  submitScreener:(body: ScreenerInput): Promise<PublicSubmissionResponse> =>
    apiClient.post<PublicSubmissionResponse>('/public/screener', body),

  submitProductDemo: (body: ProductDemoInput): Promise<PublicSubmissionResponse> =>
    apiClient.post<PublicSubmissionResponse>('/public/product-demo', body),

  submitFeatureNomination: (body: FeatureNominationInput): Promise<PublicSubmissionResponse> =>
    apiClient.post<PublicSubmissionResponse>('/public/resources/feature-suggestions', body),

  submitStartup: (body: StartupInput): Promise<PublicSubmissionResponse> =>
    apiClient.post<PublicSubmissionResponse>('/public/startup', body),

  submitCareerRestart: (body: CareerRestartInput): Promise<PublicSubmissionResponse> =>
    apiClient.post<PublicSubmissionResponse>('/public/career-restart', body),

  submitExFounderInquiry: (body: ExFounderInquiryInput, file: File | null): Promise<PublicSubmissionResponse> => {
    const formData = new FormData();
    for (const [key, value] of Object.entries(body)) formData.append(key, value);
    // The backend accepts an optional resume under the multipart field "file".
    if (file) formData.append('file', file);
    return postMultipart<PublicSubmissionResponse>('/public/ex-founder-inquiries', formData);
  },
  regions: (): Promise<RegionItem[]> => apiClient.get('/public/regions'),
  cities: (region: string): Promise<MasterDataItem[]> =>
    apiClient.get('/public/cities', { query: { region } }),
  industries: (): Promise<MasterDataItem[]> => apiClient.get('/public/industries'),
  jobCategories: (): Promise<MasterDataItem[]> => apiClient.get('/public/job-categories'),
  experienceLevels: (): Promise<MasterDataItem[]> => apiClient.get('/public/experience-levels'),
  employmentTypes: (): Promise<MasterDataItem[]> => apiClient.get('/public/employment-types'),
  workArrangements: (): Promise<MasterDataItem[]> => apiClient.get('/public/work-arrangements'),
  companySizes: (): Promise<MasterDataItem[]> => apiClient.get('/public/company-sizes'),
  resourceTypes: (): Promise<MasterDataItem[]> => apiClient.get('/public/resource-types'),

  listJobs: (filters: JobsListFilters): Promise<JobsListEnvelope> =>
    apiClient.get<JobsListEnvelope>('/public/jobs', { query: { ...filters }, raw: true }),
  getJob: (slug: string): Promise<JobDetail> => apiClient.get<JobDetail>(`/public/jobs/${encodeURIComponent(slug)}`),

  listResources: (page = 1, page_size = 100): Promise<ResourcesListEnvelope> =>
    apiClient.get<ResourcesListEnvelope>('/public/resources', {
      query: { page, page_size },
      raw: true,
    }),
  getResource: (slug: string): Promise<PublicResourceDetail> =>
    apiClient.get<PublicResourceDetail>(`/public/resources/${encodeURIComponent(slug)}`),

  submitCompanySignup: (body: CompanySignupBody): Promise<CompanySignupResponse> =>
    apiClient.post<CompanySignupResponse>('/public/companies/signup', body),

  submitInquiry: (body: { name: string; email: string; phone?: string; message: string }): Promise<{ id: string; created_at: string }> =>
    apiClient.post<{ id: string; created_at: string }>('/public/inquiries', body),
};

// ---------- Candidate-side write helpers ----------

export const candidates = {
  apply: (input: CreateApplicationInput): Promise<CreatedApplication> =>
    apiClient.post<CreatedApplication>('/candidates/me/applications', input),
};

// ---------- Candidate Dashboard (Phase 7-dashboard-b) ----------
// Typed wrappers for the 18 endpoints under /candidates/me/* added in
// Phase 7-dashboard-a. Phase 7-dashboard-b consumes the profile, resumes, and
// recovery surfaces; applications / saved-jobs / preferences / delete come in
// Phase 7-dashboard-c.

export interface MasterDataRef {
  id: string;
  slug: string;
  name: string;
}

export interface CurrencyRef extends MasterDataRef {
  code: string;
  symbol: string;
}

export interface SkillRef {
  id: string;
  slug: string;
  name: string;
}

export interface ResumeListItem {
  id: string;
  original_filename: string;
  file_type: 'PDF' | 'DOCX';
  file_size_bytes: number;
  is_primary: boolean;
  uploaded_at: string;
}

/**
 * Shape of the GET /candidates/me/profile envelope. Mirrors the backend service's
 * serialization in `candidate-profile.service.ts`. Region + email + status are
 * read-only display fields; the PATCH body forbids them.
 */
export interface CandidateProfile {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  region: { id: string; slug: RegionSlug; name: string };
  linkedin_url: string | null;
  portfolio_url: string | null;
  github_url: string | null;
  current_role: string | null;
  current_company: string | null;
  total_experience_months: number | null;
  summary: string | null;
  experience_level: MasterDataRef | null;
  notice_period: MasterDataRef | null;
  expected_salary_min: number | null;
  expected_salary_max: number | null;
  expected_salary_currency: CurrencyRef | null;
  expected_salary_pay_period: MasterDataRef | null;
  willing_to_relocate: boolean;
  // Declaration consents (May 2026). RTR is optional yes/no with a timestamp;
  // fraud is mandatory yes-only and the timestamp itself is the signal.
  rtr_consent: boolean | null;
  rtr_consent_at: string | null;
  fraud_declaration_consent_at: string | null;
  profile_completed: boolean;
  soft_skills: string[];
  tools: string[];
  certifications: string[];
  skills: SkillRef[];
  parsed_resume_snapshot: ParsedResumeSnapshot | null;
  has_primary_resume: boolean;
  primary_resume: {
    id: string;
    original_filename: string;
    file_type: 'PDF' | 'DOCX';
    uploaded_at: string;
  } | null;
  email_verified_at: string | null;
  status: 'ACTIVE' | 'SUSPENDED';
}

/**
 * AI parser output shape — must mirror `ParsedResumeData` on the backend
 * (`backend/src/modules/ai/providers/ai-provider.ts`). Personal contact fields
 * are nested under `personal`; reading them at the top level yields `undefined`
 * and silently blanks the form (the bug this hotfix addresses for full_name + phone).
 *
 * TODO: other field-name drift between this type and the backend remains
 * (current_role vs backend `current_title`, total_experience_months vs
 * `years_experience`, work_history.start_date vs `start_year`/`end_year`,
 * education.field vs `field_of_study`, certifications shape). Those reads
 * silently produce empty values today and are out of scope for this hotfix —
 * tracked as a separate cleanup.
 */
export interface ParsedResumeData {
  personal?: {
    full_name?: string | null;
    email?: string | null;
    phone?: string | null;
    linkedin_url?: string | null;
    github_url?: string | null;
    portfolio_url?: string | null;
  };
  current_role?: string | null;
  current_company?: string | null;
  summary?: string | null;
  total_experience_months?: number | null;
  tech_skills?: string[];
  soft_skills?: string[];
  tools?: string[];
  certifications?: string[];
  work_history?: Array<{
    title?: string | null;
    company?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    description?: string | null;
  }>;
  education?: Array<{
    degree?: string | null;
    school?: string | null;
    field?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }>;
}

/**
 * Snapshot stored on the Candidate row — a subset of the parser output kept for
 * display on My Profile. The frontend treats it as opaque (read-only display).
 */
export interface ParsedResumeSnapshot {
  work_history?: ParsedResumeData['work_history'];
  education?: ParsedResumeData['education'];
  [k: string]: unknown;
}

export interface ParseResumeResponse {
  resume_id: string;
  parsed: ParsedResumeData;
  matched_skill_ids: string[];
  unmatched_skill_names: string[];
}

export interface UpdateProfileBody {
  full_name?: string;
  phone?: string | null;
  linkedin_url?: string | null;
  portfolio_url?: string | null;
  github_url?: string | null;
  current_role?: string | null;
  current_company?: string | null;
  total_experience_months?: number | null;
  summary?: string | null;
  experience_level_id?: string | null;
  notice_period_id?: string | null;
  expected_salary_min?: number | null;
  expected_salary_max?: number | null;
  expected_salary_currency_id?: string | null;
  expected_salary_pay_period_id?: string | null;
  willing_to_relocate?: boolean;
  /** Declaration consents (May 2026). Backend stamps `*_at` when these are present. */
  rtr_consent?: boolean | null;
  /** Mandatory; only `true` is accepted by the backend Zod schema. */
  fraud_declaration_consent?: true;
  soft_skills?: string[];
  tools?: string[];
  certifications?: string[];
  skill_ids?: string[];
  parsed_resume_snapshot?: ParsedResumeSnapshot | null;
  /** Audit-only hint when this PATCH flips profile_completed=TRUE. */
  completion_context?: { gate?: 'soft' | 'hard'; via?: 'parsed' | 'manual' };
}

/**
 * Multipart helper for the resume upload + parse endpoints. The generic JSON
 * helpers don't carry FormData; this one mirrors the same CSRF + envelope rules.
 */
async function postMultipart<T>(path: string, formData: FormData): Promise<T> {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const isPublicPath = path.startsWith('/public/');
  const headers: Record<string, string> = { Accept: 'application/json' };
  const csrf = readCsrfCookie();
  if (csrf) headers[CSRF_HEADER] = csrf;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: isPublicPath ? 'omit' : 'include',
      headers,
      body: formData,
    });
  } catch {
    throw new ApiError(
      {
        code: 'NETWORK_ERROR',
        message: 'Could not reach the QuantumHire server. Check your connection and try again.',
      },
      0,
    );
  }
  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    const envelope = payload as { error?: ApiErrorPayload } | null;
    if (envelope && envelope.error) {
      if (envelope.error.code === 'RATE_LIMITED') {
        throw new RateLimitError(envelope.error, res.status, parseRetryAfter(res, envelope.error));
      }
      throw new ApiError(envelope.error, res.status);
    }
    if (res.status === 429) {
      throw new RateLimitError(
        { code: 'RATE_LIMITED', message: 'Too many requests. Please slow down.' },
        res.status,
        parseRetryAfter(res, null),
      );
    }
    throw new ApiError(
      { code: 'INTERNAL_ERROR', message: 'Something went wrong. Please try again.' },
      res.status,
    );
  }
  const dataEnv = payload as { data?: T } | null;
  return (dataEnv && 'data' in dataEnv ? (dataEnv.data as T) : (payload as T));
}

export const candidateApi = {
  // ----- Profile -----
  profile: {
    get: (): Promise<CandidateProfile> =>
      apiClient.get<CandidateProfile>('/candidates/me/profile'),
    update: (body: UpdateProfileBody): Promise<CandidateProfile> =>
      apiClient.patch<CandidateProfile>('/candidates/me/profile', body),
    /** Multipart first-parse path — uploads a new resume and runs AI parse. */
    parseUpload: (file: File): Promise<ParseResumeResponse> => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      return postMultipart<ParseResumeResponse>('/candidates/me/parse-resume', fd);
    },
    /** JSON re-parse path — runs AI parse over an existing resume. */
    parseExisting: (resumeId: string): Promise<ParseResumeResponse> =>
      apiClient.post<ParseResumeResponse>('/candidates/me/parse-resume', { resume_id: resumeId }),
  },

  // ----- Resumes -----
  resumes: {
    list: (): Promise<ResumeListItem[]> =>
      apiClient.get<ResumeListItem[]>('/candidates/me/resumes'),
    add: (file: File): Promise<ResumeListItem> => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      return postMultipart<ResumeListItem>('/candidates/me/resumes', fd);
    },
    markPrimary: (id: string): Promise<ResumeListItem> =>
      apiClient.patch<ResumeListItem>(`/candidates/me/resumes/${encodeURIComponent(id)}/primary`),
    remove: (id: string): Promise<void> =>
      apiClient.delete<void>(`/candidates/me/resumes/${encodeURIComponent(id)}`),
    downloadUrl: (id: string): Promise<{ url: string; expires_at: string; filename: string }> =>
      apiClient.get(`/candidates/me/resumes/${encodeURIComponent(id)}/download-url`),
  },

  // ----- Applications (Phase 7-dashboard-c) -----
  applications: {
    list: (
      params: ApplicationsListParams = {},
    ): Promise<PaginatedEnvelope<ApplicationListItem>> =>
      apiClient.get<PaginatedEnvelope<ApplicationListItem>>('/candidates/me/applications', {
        query: { ...params },
        raw: true,
      }),
    get: (id: string): Promise<ApplicationDetail> =>
      apiClient.get<ApplicationDetail>(`/candidates/me/applications/${encodeURIComponent(id)}`),
    /**
     * Cheap "have I applied to this job?" probe. The public job-detail endpoint can't
     * answer this for signed-in candidates because the website strips cookies on
     * /public/* paths (see comment in `request()` above) — so JobDetailPage hydrates
     * its apply state via this authenticated route instead. Errors → false (caller
     * keeps default Apply UI; a real 409 would surface during apply submission).
     */
    hasAppliedToJob: async (jobId: string): Promise<boolean> => {
      try {
        const env = await apiClient.get<PaginatedEnvelope<ApplicationListItem>>(
          '/candidates/me/applications',
          { query: { job_id: jobId, page_size: 1 }, raw: true },
        );
        return (env.data?.length ?? 0) > 0;
      } catch {
        return false;
      }
    },
  },

  // ----- Saved jobs (Phase 7-dashboard-c) -----
  savedJobs: {
    list: (
      params: { page?: number; page_size?: number } = {},
    ): Promise<PaginatedEnvelope<SavedJobItem>> =>
      apiClient.get<PaginatedEnvelope<SavedJobItem>>('/candidates/me/saved-jobs', {
        query: { ...params },
        raw: true,
      }),
    save: (jobId: string): Promise<{ saved_at: string }> =>
      apiClient.post<{ saved_at: string }>('/candidates/me/saved-jobs', { job_id: jobId }),
    unsave: (jobId: string): Promise<void> =>
      apiClient.delete<void>(`/candidates/me/saved-jobs/${encodeURIComponent(jobId)}`),
  },

  // ----- Preferences (Phase 7-dashboard-c) -----
  preferences: {
    get: (): Promise<CandidatePreferences> =>
      apiClient.get<CandidatePreferences>('/candidates/me/preferences'),
    update: (body: Partial<CandidatePreferences>): Promise<CandidatePreferences> =>
      apiClient.patch<CandidatePreferences>('/candidates/me/preferences', body),
  },

  // ----- Account / recovery -----
  account: {
    /**
     * Restore a soft-deleted account. Authenticated solely by the recovery cookie
     * that the signin endpoint mints when `deleted_at IS NOT NULL`. Mints normal
     * session cookies on success.
     */
    recover: (): Promise<CandidateMe> =>
      apiClient.post<{ candidate: CandidateMe }>('/candidates/me/recover').then((env) => env.candidate),
    /**
     * Soft-delete the candidate account after a "type DELETE" confirmation. Universal
     * for both email/password and OAuth-only candidates (the JWT cookie is the auth).
     * Server clears the candidate session cookies on success — the SPA must
     * subsequently treat the user as signed-out. Uses a body-bearing DELETE which the
     * generic `apiClient.delete` doesn't expose, so we go through the local
     * `deleteWithBody` helper.
     */
    delete: (): Promise<void> =>
      deleteWithBody<void>('/candidates/me', { confirmation: 'DELETE' }),
  },

  // ----- Public master-data passthroughs used by the dashboard form -----
  publicSkills: (q: string): Promise<MasterDataRef[]> =>
    apiClient.get<MasterDataRef[]>('/public/skills', { query: { q } }),
  /**
   * Hydrate skill names from a known set of ids. Used by the My Profile re-parse
   * flow so chips can render names for matched_skill_ids that aren't in the
   * SkillPicker's bootstrap top-50 catalog. Backend caps `ids` at 200 per request.
   */
  publicSkillsByIds: (ids: string[]): Promise<MasterDataRef[]> => {
    if (ids.length === 0) return Promise.resolve([]);
    return apiClient.get<MasterDataRef[]>('/public/skills', { query: { ids: ids.join(',') } });
  },
};

async function deleteWithBody<T>(path: string, body: unknown): Promise<T> {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  const csrf = readCsrfCookie();
  if (csrf) headers[CSRF_HEADER] = csrf;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(
      {
        code: 'NETWORK_ERROR',
        message: 'Could not reach the QuantumHire server. Check your connection and try again.',
      },
      0,
    );
  }
  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    const envelope = payload as { error?: ApiErrorPayload } | null;
    if (envelope && envelope.error) {
      if (envelope.error.code === 'RATE_LIMITED') {
        throw new RateLimitError(envelope.error, res.status, parseRetryAfter(res, envelope.error));
      }
      throw new ApiError(envelope.error, res.status);
    }
    throw new ApiError(
      { code: 'INTERNAL_ERROR', message: 'Something went wrong. Please try again.' },
      res.status,
    );
  }
  const dataEnv = payload as { data?: T } | null;
  return (dataEnv && 'data' in dataEnv ? (dataEnv.data as T) : (payload as T));
}

// ---------- Phase 7-dashboard-c shared types ----------

export interface PaginatedEnvelope<T> {
  data: T[];
  meta: PaginationMeta;
}

/** Lifecycle states a job can be in from the candidate's POV (saved_jobs surface). */
export type SavedJobStatus =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'UNPUBLISHED'
  | 'ON_HOLD'
  | 'CLOSED'
  | 'DELETED'
  /** Saved Jobs only (step 5d): the job's company is no longer listed, so the website hides it. */
  | 'UNAVAILABLE';

export interface ApplicationListItem {
  id: string;
  /** Candidate-facing status (recruitment workflow §5.5) — includes UNDER_PROCESSING. */
  status: CandidateApplicationStatusCode;
  applied_at: string;
  job: ApplicationJobRef | null;
  resume: { id: string; original_filename: string; file_type: 'PDF' | 'DOCX' } | null;
}

export interface ApplicationJobRef {
  id: string;
  slug: string;
  title: string;
  status: SavedJobStatus;
  region: JobLocation & { id: string };
  cities: (JobLocation & { id: string })[];
}

export interface ApplicationDetail extends ApplicationListItem {
  cover_note: string | null;
  job:
    | (ApplicationJobRef & {
        description_html: string;
        published_at: string | null;
        valid_through: string | null;
      })
    | null;
  resume:
    | { id: string; original_filename: string; file_type: 'PDF' | 'DOCX'; uploaded_at: string }
    | null;
  status_history: ApplicationStatusHistoryRow[];
}

export interface ApplicationStatusHistoryRow {
  id: string;
  from_status: ApplicationStatusCode | null;
  to_status: ApplicationStatusCode;
  changed_by_user_type: 'CANDIDATE' | 'COMPANY_USER' | 'ADMIN_USER' | 'SYSTEM';
  occurred_at: string;
}

/** Mirror of `ApplicationStatus` in shared/lib/applicationStatus — re-declared to avoid
 *  cross-package import noise in this types file. */
export type ApplicationStatusCode =
  | 'APPLIED'
  | 'UNDER_REVIEW'
  | 'SHORTLISTED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'HIRED';

/** Mirror of `CandidateApplicationStatus` in shared/lib/applicationStatus — the status a
 *  candidate sees (recruitment workflow §5.5): the client status, or UNDER_PROCESSING while the
 *  application is in QuantumHire's internal funnel. History rows stay `ApplicationStatusCode`. */
export type CandidateApplicationStatusCode = ApplicationStatusCode | 'UNDER_PROCESSING';

export interface ApplicationsListParams {
  /** Filters on the candidate-facing status, so the results match the badges. */
  status?: CandidateApplicationStatusCode;
  job_id?: string;
  page?: number;
  page_size?: number;
}

export interface SavedJobItem {
  job_id: string;
  saved_at: string;
  job: {
    id: string;
    slug: string;
    title: string;
    status: SavedJobStatus;
    published_at: string | null;
    valid_through: string | null;
    region: JobLocation & { id: string };
    cities: (JobLocation & { id: string })[];
  } | null;
}

export interface CandidatePreferences {
  allow_recruiter_messages: boolean;
  share_profile_with_network: boolean;
  email_weekly_job_recommendations: boolean;
}
